import type { VotingDetail } from '../../api/DigiobcanApi';
import { answerMeta, type VotingAnswers } from '../../lib/votings';
import { __ } from '../../utils';
import { AnswerSummary, QuestionSummaryCard } from './QuestionSummary';
import { Box, Stack } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  questions: VotingDetail['questions'];
  answers: VotingAnswers;
};

/** Zelená fajka - odovzdaný lístok je vždy platný, takže tu nie je žltý stav. */
const CheckMark: React.FC = () => (
  <Box
    aria-hidden
    sx={({ palette, shape }) => ({
      flex: 'none',
      width: 22,
      height: 22,
      display: 'grid',
      placeItems: 'center',
      borderRadius: `${shape.borderRadiusFull}px`,
      backgroundColor: palette.green.main,
      color: palette.common.white,
    })}
  >
    <Box
      component='svg'
      viewBox='0 0 24 24'
      width={14}
      height={14}
      fill='none'
      stroke='currentColor'
      strokeWidth={3}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M5 13l4 4L19 7' />
    </Box>
  </Box>
);

/**
 * Odovzdaný lístok - „takto ste hlasovali".
 *
 * Predtým to bol ten istý formulár, len predvyplnený a zamknutý. Vyzeralo to
 * ako obrazovka, na ktorej sa dá niečo zmeniť, a nedalo: prepínače boli šedé,
 * pole „Iné" nešlo dopísať a tlačidlo odoslania zmizlo bez vysvetlenia.
 * Odovzdaný hlas je zápis, nie formulár.
 *
 * **Kreslí ho ten istý komponent ako kontrolu pred odoslaním**
 * ([[QuestionSummary]]): odpoveď ako štruktúra podľa typu, dlhá škála
 * zoskupená podľa hodnotenia zhora dole. Nahradilo to plochý zoznam riadkov
 * z `answerLines`, v ktorom sa pri otázke s dvadsiatimi šiestimi tvrdeniami
 * vlastná odpoveď nedala nájsť. Nevybrané možnosti sa už nevypisujú - lístok
 * ukazuje, čo účet **odpovedal**, rovnako ako tá kontrola.
 */
const VotingTicket: React.FC<Props> = ({ questions, answers }) => (
  <Stack gap={1.5}>
    {questions.map((question, index) => (
      <QuestionSummaryCard
        key={question.question_id}
        mark={<CheckMark />}
        meta={`${__('Otázka')} ${index + 1} · ${answerMeta(question, answers[question.question_id])}`}
        title={question.title}
      >
        <AnswerSummary
          question={question}
          answers={answers[question.question_id]}
        />
      </QuestionSummaryCard>
    ))}
  </Stack>
);

export default VotingTicket;
